import { Component, OnInit, HostListener, Input, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { IFormNavItem } from '@amobee/component-library';
import { FormGroup, AbstractControl, FormArray } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { FormTabName } from 'src/app/application/campaigns/campaign-edit/constants/FormTabNames.const';

@Component({
  selector: 'form-side-nav-bar',
  templateUrl: './form-side-nav-bar.component.html',
  styleUrls: ['./form-side-nav-bar.component.scss']
})
export class FormSideNavBarComponent implements OnInit, OnDestroy {
  @Input() public tabNames: Array<FormTabName> = [];
  @Input() group: FormGroup;
  @Input() showStatusIcon: boolean = false;
  @Input() showIndex: boolean = true;
  pageHeaderPos: number = 20;
  pageFooterHeight: number;
  tabs: Array<IFormNavItem> = [];
  activeTabName: string;
  tabNameFormControlsMapping: { [key: string]: Array<AbstractControl> } = {};
  tabHasMarkedTouch: { [key: string]: boolean } = {};
  tabValidities: { [key: string]: Array<string> } = {};
  formControlSubscriptions: Array<Subscription> = [];

  @Input() public set submitted(submitted: boolean) {
    if (submitted) {
      this.tabNames.forEach((tab: FormTabName, index: number) => {
        this.markAllTabControlsTouched(tab.formNavLabel, index);
      });
    }
  }

  constructor() { }

  ngOnInit() {
    if (_.size(this.tabNames)) {
      this.tabNames.forEach((tab: FormTabName, tabIndex: number) => {
        this.tabs.push({
          title: tab.formNavLabel,
          status: 'NONE',
          onClick: (item: IFormNavItem) => {
            this.clickOnTabScroll(_.find(this.tabNames, { 'formNavLabel': item.title }).id);
          },
          leftElementText: this.showIndex ? (tabIndex + 1).toString() : undefined
        });
        let formControls: Array<AbstractControl> = [];
        let validities: Array<string> = [];
        tab.controls.forEach((path: string[], controlIndex: number) => {
          let control: AbstractControl = this.group.get(path);
          formControls.push(control);
          if (this.showStatusIcon) {
            validities.push(control.status);
            this.formControlSubscriptions.push(control.statusChanges.pipe(debounceTime(300)).subscribe((status: any) => {
              validities[controlIndex] = status;
              if (this.tabHasMarkedTouch[tab.formNavLabel]) {
                this.tabs[tabIndex].status = status == 'INVALID' ? 'ERROR' : validities.includes('INVALID') ? 'ERROR' : 'OK';
                this.tabs = [].concat(this.tabs);
              }
            }));
          }
        });
        if (this.showStatusIcon) {
          // push last item to validities to indicate group validation
          validities.push('VALID');
        }
        this.tabNameFormControlsMapping[tab.formNavLabel] = formControls;
        this.tabValidities[tab.formNavLabel] = validities;
      });
      if (this.showStatusIcon) {
        this.formControlSubscriptions.push(this.group.statusChanges.pipe(debounceTime(300)).subscribe((status: any) => {
          this.tabNames.forEach((tab: FormTabName, tabIndex: number) => {
            if (tab.groupValidationKeys.length > 0) {
              let isGroupInTabValid: boolean = this.checkGroupValidationForTab(tabIndex);
              let validities = this.tabValidities[tab.formNavLabel];
              validities[validities.length - 1] = isGroupInTabValid ? 'VALID' : 'INVALID';
              if (this.tabHasMarkedTouch[tab.formNavLabel]) {
                this.tabs[tabIndex].status = !isGroupInTabValid ? 'ERROR' : validities.includes('INVALID') ? 'ERROR' : 'OK';
                this.tabs = [].concat(this.tabs);
              }
            }
          });
        }));
      }
      this.setActiveTab(this.tabNames[0].formNavLabel);
    }
  }

  ngAfterViewInit() {
    this.pageFooterHeight = document.querySelector('.footer-container').clientHeight;
  }

  ngOnDestroy() {
    this.formControlSubscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.checkActiveTab();
  }

  clickOnTabScroll(id: string): void {
    let el = document.getElementById(id);
    window.scrollTo({ top: el.offsetTop - this.pageHeaderPos, behavior: 'smooth' });
  }

  setActiveTab(tabRef: string) {
    this.activeTabName = tabRef;
  }

  checkActiveTab() {
    if (window.pageYOffset === 0) {
      this.setActiveTab(this.tabNames[0].formNavLabel);
      return;
    }
    let activeTab: string, previousTab: string;
    this.tabNames.forEach((tab: FormTabName, index: number) => {
      let el = document.querySelector(`#${tab.id} .acl-Accordion-title`);
      if (!activeTab && this.IsElementInView(el)) {
        activeTab = tab.formNavLabel;
      }
      if (el.getBoundingClientRect().top < 0) {
        previousTab = tab.formNavLabel;
        this.markAllTabControlsTouched(tab.formNavLabel, index);
      }
    });
    if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight) {
      // reach bottom, set the last tab as touched
      let tab = this.tabNames[this.tabNames.length - 1];
      if (activeTab === tab.formNavLabel) {
        this.markAllTabControlsTouched(tab.formNavLabel, this.tabNames.length - 1);
      }
    }
    this.setActiveTab(activeTab || previousTab);
  }

  markAllTabControlsTouched(tabLabel: string, tabIndex: number) {
    if (!this.tabHasMarkedTouch[tabLabel]) {
      let isInvalid: boolean = !this.checkGroupValidationForTab(tabIndex);
      this.tabNameFormControlsMapping[tabLabel].forEach((control: AbstractControl) => {
        if (!control.touched) {
          this.markAllDescendantsAsTouched(control);
        }
        isInvalid = isInvalid || control.invalid;
      });
      this.tabHasMarkedTouch[tabLabel] = true;
      if (this.tabs[tabIndex].status === 'NONE' && this.showStatusIcon) {
        this.tabs[tabIndex].status = isInvalid ? 'ERROR' : 'OK';
        this.tabs = [].concat(this.tabs);
      }
    }
  }

  checkGroupValidationForTab(tabIndex: number): boolean {
    if (this.tabNames[tabIndex].groupValidationKeys.length > 0) {
      if (this.group.errors) {
        return _.intersection(Object.keys(this.group.errors), this.tabNames[tabIndex].groupValidationKeys).length === 0;
      }
    }
    return true;
  }

  IsElementInView(el: Element) {
    const scroll = window.scrollY || window.pageYOffset
    const boundsTop = el.getBoundingClientRect().top + scroll

    const viewport = {
      top: scroll,
      bottom: scroll + window.innerHeight - this.pageFooterHeight
    }

    const bounds = {
      top: boundsTop,
      bottom: boundsTop + el.clientHeight
    }

    return (bounds.bottom >= viewport.top && bounds.bottom <= viewport.bottom)
      || (bounds.top <= viewport.bottom && bounds.top >= viewport.top);
  }

  markAllDescendantsAsTouched(control: AbstractControl, onlySelf: boolean = false) {
    control.markAsTouched({ onlySelf: true });

    if (!onlySelf) {
      if (control instanceof FormGroup) {
        Object.values((control as FormGroup).controls).forEach(val => this.markAllDescendantsAsTouched(val));
      } else if (control instanceof FormArray) {
        (control as FormArray).controls.forEach(val => this.markAllDescendantsAsTouched(val));
      }
    }
  }

}